import React, { useEffect } from "react";

/**
 * AuthBackground - Reusable background component with the same design as Login page
 * Features: gradient, animated blobs, decorative waves (hidden on mobile), floating hearts
 * Props:
 *   - children: React components to render on top of the background
 *   - className: Additional classes for the container
 */
function AuthBackground({ children, className = "" }) {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const updateViewportUnit = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--auth-vh", `${vh}px`);
    };

    updateViewportUnit();
    window.addEventListener("resize", updateViewportUnit);

    return () => window.removeEventListener("resize", updateViewportUnit);
  }, []);

  return (
    <div
      className={`min-h-screen flex flex-col overflow-x-hidden bg-gradient-to-br from-pink-50 via-purple-50 to-indigo-50 ${className}`}
    >
      {/* Animated background blobs - responsive for all screens */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 -left-4 w-[16vw] min-w-[256px] max-w-[384px] h-[16vw] min-h-[256px] max-h-[384px] bg-purple-200 rounded-full mix-blend-multiply filter blur-2xl sm:blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-0 -right-4 w-[16vw] min-w-[256px] max-w-[384px] h-[16vw] min-h-[256px] max-h-[384px] bg-pink-200 rounded-full mix-blend-multiply filter blur-2xl sm:blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-[16vw] min-w-[256px] max-w-[384px] h-[16vw] min-h-[256px] max-h-[384px] bg-indigo-200 rounded-full mix-blend-multiply filter blur-2xl sm:blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Top-Right Corner Wave Background - HIDDEN BELOW 1031px */}
      <div className="corner-wave-top absolute top-0 right-[-18vw] w-[45vw] h-[45vh] z-0 pointer-events-none rotate-[40deg]">
        <svg
          viewBox="0 0 800 800"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="w-full h-full filter drop-shadow-lg transform rotate-180"
        >
          <path
            d="M0,300 Q100,200 240,240 Q380,280 520,250 Q660,210 800,260 L800,800 L0,800 Z"
            fill="#9333EA"
            opacity="0.8"
          />
          <path
            d="M0,400 Q120,310 280,360 Q450,420 600,370 Q720,330 800,370 L800,800 L0,800 Z"
            fill="#C084FC"
            opacity="0.75"
          />
          <path
            d="M0,500 Q150,420 320,470 Q500,530 680,480 L800,800 L0,800 Z"
            fill="#DDD6FE"
            opacity="0.6"
          />
        </svg>
      </div>

      {/* Bottom Corner Wave Background - HIDDEN BELOW 1031px */}
      <div className="corner-wave-bottom fixed bottom-[-20px] left-[-12vw] w-[35vw] h-[38vh] z-0 pointer-events-none rotate-[60deg]">
        <svg
          viewBox="0 0 800 800"
          xmlns="http://www.w3.org/2000/svg"
          preserveAspectRatio="none"
          className="w-full h-full filter drop-shadow-lg"
        >
          <path
            d="M0,300 Q100,200 240,240 Q380,280 520,250 Q660,210 800,260 L800,800 L0,800 Z"
            fill="#9333EA"
            opacity="0.8"
          />
          <path
            d="M0,400 Q120,310 280,360 Q450,420 600,370 Q720,330 800,370 L800,800 L0,800 Z"
            fill="#C084FC"
            opacity="0.75"
          />
          <path
            d="M0,500 Q150,420 320,470 Q500,530 680,480 L800,800 L0,800 Z"
            fill="#DDD6FE"
            opacity="0.6"
          />
        </svg>
      </div>

      {/* Floating hearts - visible on all screens */}
      <div className="fixed inset-0 pointer-events-none select-none opacity-5 sm:opacity-8 md:opacity-10">
        {["💕", "❤️", "💖", "💗", "💝", "🩷", "💞"].map((heart, i) => (
          <span
            key={i}
            className="absolute text-xl sm:text-2xl animate-float"
            style={{
              left: `${5 + ((i * 12) % 90)}%`,
              top: `${10 + ((i * 8) % 80)}%`,
              animationDelay: `${i * 1.5}s`,
              animationDuration: `${12 + i * 2}s`,
            }}
          >
            {heart}
          </span>
        ))}
      </div>

      {/* Small hearts for mobile */}
      <div className="sm:hidden fixed inset-0 pointer-events-none select-none opacity-10">
        {["❤️", "💕", "💖"].map((heart, i) => (
          <span
            key={i}
            className="absolute text-lg animate-float"
            style={{
              left: `${10 + i * 30}%`,
              top: `${15 + i * 25}%`,
              animationDelay: `${i * 2}s`,
              animationDuration: `${10 + i * 3}s`,
            }}
          >
            {heart}
          </span>
        ))}
      </div>

      {/* Content - flex-grow to push footer down */}
      <div className="relative z-10 flex-1 flex flex-col">{children}</div>

      {/* Custom animations */}
      <style jsx>{`
        /* Hide corner waves at 1280px and below */
        @media (max-width: 1280px) {
          .corner-wave-top,
          .corner-wave-bottom {
            display: none !important;
          }
        }

        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(20px, -20px) scale(1.05);
          }
          66% {
            transform: translate(-15px, 15px) scale(0.95);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 10s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }

        @keyframes float {
          0% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(5deg);
          }
          100% {
            transform: translateY(0px) rotate(0deg);
          }
        }
        .animate-float {
          animation: float 8s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}

export default AuthBackground;
